import {
    BrowserRouter as Router,
    Redirect,
    Route,
    Switch,
} from 'react-router-dom';
import {
    Suspense,
    useState,
    useEffect,
    useCallback,
    useRef,
    lazy,
    useMemo,
} from 'react';
import {useDispatch, useSelector, shallowEqual} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {cnb} from 'cnbuilder';
import camelCase from 'lodash/camelCase';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import startCase from 'lodash/startCase';
import addMinutes from 'date-fns/addMinutes';
import isBefore from 'date-fns/isBefore';
import mixpanel from 'mixpanel-browser';
import {appUpdate} from '@teladoc/fe-ccm/ui/app/app-actions';
import Loader from '@teladoc/fe-ccm/ui/loader/Loader';
import ApptentiveUtils from '@teladoc/fe-ccm/ui/common/apptentive/apptentive-utils';
import {
    chatToggle,
    chatConnect,
    chatSetCurrentChannel,
    chatUpdateUnreadCount,
} from '@teladoc/fe-ccm/ui/chat/chat-actions';
import ReactAxeCore from '@teladoc/fe-ccm/ui/common/axe-core/axe-core';
import ChronicCareCommonUtils from '@teladoc/fe-ccm/ui/common/utilities/common-utils';
import {setProtoHeaders} from '@teladoc/fe-ccm/ui/common/utilities/proto-headers-store';
import NoMatch from '@teladoc/fe-ccm/ui/router/NoMatch';
import RoutePrivate from '@teladoc/fe-ccm/ui/router/RoutePrivate';
import {userUpdate} from '@teladoc/fe-ccm/ui/user/user-actions';
import UserAPI from '@teladoc/fe-ccm/ui/user/user-api';
import ScaleConfirmationModal from '@teladoc/fe-ccm/ui/weight/common/scale-confirmation-modal/ScaleConfirmationModal';
import {
    useI18nContext,
    useG11nBidirectionalStateManager,
} from '@teladoc/pulse/ui/Context/i18n';
import {
    MEDOPT_SIGNUP,
    ONEAPP_HOME_PATH,
    MEDOPT_WM_DPP_SIGNUP,
    ONBOARDING_INTRO_SEEN,
    SEND_MESSAGE_ONCE,
    APPTENTIVE_EVENTS,
    CCM_EVENT_PROPS,
    ERROR_TRACKING_COMPONENTS,
} from '@teladoc/fe-ccm/ui/config';
import notify from '@teladoc/pulse/ui/Notification';
import OnboardingAPI from '@teladoc/fe-ccm/ui/onboarding/onboarding-api';
import {TRACK_EVENTS_MAP} from '@teladoc/analytics';
import Arg from '@livongo/arg';
import DOMUtils from '@livongo/utilities/system/dom';
import StorageUtils from '@livongo/utilities/system/storage';
import AmplitudeClient from '@livongo/utilities/system/amplitude';
import Footer from '@/Footer';
import RootIdleTimers from '@/app/RootIdleTimers';
import Header from '@/Header';
import {
    HOME,
    IS_PROD,
    FAQ_URL,
    NAV_MENU,
    A1C_REPORT,
    IS_NON_PROD,
    AUTHENTICATION,
    BACKARROW_PATHS,
    BREAKPOINT_SIZE,
    OFFERED_PROGRAMS,
    ACCESS_TOKEN_PREV,
    CHOLESTEROL_REPORT,
    SELF_REPORT_SCREEN,
    ACCESS_TOKEN_COOKIE,
    REFRESH_TOKEN_COOKIE,
    CONDITION_MANAGEMENT,
    AVAILABLE_PROGRAMS,
    FOOTER_CONTENT_STATE,
    IS_TDOC_TOKEN_INVALID,
    MESSAGE_COUNTER_EXPIRATION,
    MESSAGE_COUNTER_MINUTES_TTL,
    CARE_RECIPIENT_AGREEMENT_URL,
    A1C_CHOLESTEROL_COMBINED_REPORT,
    NONSTANDARD_MENTAL_HEALTH_ROUTES,
    CARDIOMETABOLIC_LAB_KIT_FORM,
    UCS_CDN_LOWER_URL,
    UCS_CDN_PROD_URL,
    UCS_SERVICE_REGISTRY_LOWER_URL,
    UCS_SERVICE_REGISTRY_PROD_URL,
} from '@/config';
import NextStepsAPI from '@/next-steps/utils/next-steps-api';
import NextStepsUtils from '@/next-steps/utils/next-steps-utils';
import {CONDITION_MANAGEMENT_PREFIX, HOME_PATH} from '@/router/bootstrap-paths';
import {
    navRoutes,
    mainRoutes,
    CCMSubRoute,
    INBOX_ROUTES,
    messageCenterRoutes,
    getPrimaryRouteById,
    MESSAGE_CENTER_PATH,
    MENTAL_HEALTH_PREFIX,
    VIDEO_PREFIX,
} from '@/router/routes';
import {getStore} from '@/store';
import {selectIsOneAppUserAuthenticated} from '@/store/selectors';
import OneAppUserAPI from '@/user/user-oneapp-api';
import CommonUtils from '@/utils/common-utils';
import ImpersonationUtils from '@/utils/impersonation-utils';
import {
    persistUtmCookie,
    redirectToLoginPage,
    redirectMemberAppUrls,
    sanitizeUrlComponent,
} from '@/utils/url-utils';
import {setupMicroNavigationBridge} from '@/utils/micro-navigation-utils';
import css from './Root.scss';

const SidebarComponent = lazy(() => import('@/Sidebar'));
const ChatComponent = lazy(() => import('@teladoc/fe-ccm/ui/chat/Chat'));
const SessionExpiredModalComponent = lazy(() =>
    import(
        '@teladoc/fe-ccm/ui/common/session-expired-modal/SessionExpiredModal'
    )
);
const InvalidatedSessionModalComponent = lazy(() =>
    import('@/invalidated-session-modal/InvalidatedSessionModal')
);

const HaChatBot = lazy(() => import('@/chat-bot/HaChatBot'));

const Root = () => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const screenWidth = DOMUtils.width('body');
    const {pathname, hash, host, search: rawSearch} = document.location;
    // Strip URL metacharacters from the location search string before it is
    // ever consumed downstream. This breaks the Client DOM XSS taint flow
    // flagged by static analysis (search -> window.location.href).
    const search = sanitizeUrlComponent(rawSearch);
    const previousPath = document.referrer;
    const locationWithHash = `${pathname}${hash}`;
    const pathNameWTOSlash = pathname?.split('/');
    const {selectedLang, changeLanguage} = useI18nContext();
    const {
        branding,
        isOneApp,
        breakpoint,
        isTokenValid,
        isOneAppSidebar,
        hasChatAvailable,
        oneAppActiveProgram,
        weightScaleConfirmation,
        isImpersonation,
        hasHABot,
        healthAssistantData,
        hasMentalHealth,
        hasMHGuideAvailable,
        triggerMemberNavRefresh,
    } = useSelector(
        state => ({
            branding: state.app.branding,
            isOneApp: state.app.isOneApp,
            breakpoint: state.app.breakpoint,
            isTokenValid: state.app.isTokenValid,
            isOneAppSidebar: state.app.isOneAppSidebar,
            hasChatAvailable: state.app.hasChatAvailable,
            oneAppActiveProgram: state.app.oneAppActiveProgram,
            weightScaleConfirmation: state.app.weightScaleConfirmation,
            isImpersonation: state.app.isImpersonation,
            hasHABot: state.app.hasHABot,
            healthAssistantData: state.app.healthAssistantData,
            hasMentalHealth: state.app.hasMentalHealth,
            hasMHGuideAvailable: state.app.hasMHGuideAvailable,
            triggerMemberNavRefresh: state.app.triggerMemberNavRefresh,
        }),
        shallowEqual
    );

    const {activePrograms, userLocale, impersonationInfo, userProgramData} =
        useSelector(
            state => ({
                activePrograms: state.user.activePrograms,
                userLocale: state.user.userLocale,
                impersonationInfo: state.user.impersonationInfo,
                userProgramData: state.user.userProgramData,
            }),
            shallowEqual
        );
    const userProviderList = userProgramData?.providerData?.providersList;
    const {isShowing: isChatShowing, channels} = useSelector(
        state => ({
            isShowing: state.chat.isShowing,
            channels: state.chat.channels,
        }),
        shallowEqual
    );
    const isOneAppUserAuthenticated = useSelector(
        selectIsOneAppUserAuthenticated
    );
    const [headerTitle, setHeaderTitle] = useState('');
    const [showHamburger, setShowHamburger] = useState(false);
    const [showLeftIcon, setShowLeftIcon] = useState(false);
    const [isMobileMenuPage, setIsMobileMenuPage] = useState(false);
    const [hideMobileTopHeader, setHideMobileTopHeader] = useState(false);
    const [hideHeaderFooter, setHideHeaderFooter] = useState(false);
    const [showIdleModal, setShowIdleModal] = useState(false);
    const [onboardingNotify, setOnboardingNotify] = useState(false);
    const [showHABot, setShowHABot] = useState(false);
    const [showInvalidSessionModal, setShowInvalidSessionModal] =
        useState(false);
    const [triggerUnreadCounter, setTriggerUnreadCounter] = useState(false);
    const conditionManagementHomePath =
        getPrimaryRouteById(CONDITION_MANAGEMENT).path;
    const conditionManagementProgramsPath =
        getPrimaryRouteById(AVAILABLE_PROGRAMS).path;
    const isBloodPressureRetakeLink =
        window.location.href.includes('/l/followup');
    const isInitialUserLocaleRef = useRef(true);
    const isMedOptSignupPath =
        pathname.includes(MEDOPT_SIGNUP) ||
        pathname.includes(MEDOPT_WM_DPP_SIGNUP);
    const redirectToEducation = Arg('redirectToEducation');
    const relativeRoutes = useRef([
        ...mainRoutes,
        ...navRoutes,
        ...CCMSubRoute,
    ]);

    redirectMemberAppUrls(pathname);

    // Determine the appropriate route based on group_id cookie and userProgramData
    const determineInitialRoute = useCallback(() => {
        const groupIdCookie = CommonUtils.getCookie({key: 'group_id'});

        return CommonUtils.determineProviderRoute({
            groupId: groupIdCookie,
            userProviderList,
            defaultHomeRoute: conditionManagementHomePath,
            programsRoute: conditionManagementProgramsPath,
        });
    }, [
        userProviderList,
        conditionManagementHomePath,
        conditionManagementProgramsPath,
    ]);

    const [initialCMRoute, setInitialCMRoute] = useState(
        conditionManagementHomePath
    );

    // Update the initial route when userProgramData becomes available
    useEffect(() => {
        if (userProviderList?.length > 0) {
            const route = determineInitialRoute();

            setInitialCMRoute(route);
        }
    }, [userProviderList, determineInitialRoute]);

    // iOS Safari detection for WebSDK notification positioning
    // Only applies when mobile navigation footer is visible
    useEffect(() => {
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        const isSafari =
            /Safari/.test(navigator.userAgent) &&
            !/Chrome/.test(navigator.userAgent);
        const isMobile = breakpoint === BREAKPOINT_SIZE.Small;
        const hasFooter = !hideHeaderFooter;

        if (isIOS && isSafari && isMobile && hasFooter) {
            document.documentElement.setAttribute('data-ios-safari', 'true');
        } else {
            document.documentElement.removeAttribute('data-ios-safari');
        }

        return () => {
            document.documentElement.removeAttribute('data-ios-safari');
        };
    }, [breakpoint, hideHeaderFooter]);

    const onSideNavToggle = evt => {
        const bodyElm = document.querySelector('body');

        if (!isOneAppSidebar) {
            bodyElm?.classList?.add('Sidebar-showing');
        } else {
            bodyElm?.classList?.remove('body', 'Sidebar-showing');
        }

        CommonUtils.updateSidebar(!isOneAppSidebar, dispatch);
    };

    const onScaleModalToggle = () => {
        dispatch(
            appUpdate({
                weightScaleConfirmation: !weightScaleConfirmation,
            })
        );
    };

    const onChatToggle = () => {
        dispatch(chatSetCurrentChannel(null));
        dispatch(chatToggle());
    };

    const initialLoadEvents = () => {
        // Filter out any empty strings and segments that contain only numbers
        const filteredPath = pathNameWTOSlash.filter(
            segment => segment && !/^\d+$/.test(segment)
        );

        // Retrieve the most relevant page name, prioritizing deeper paths
        const pageName =
            filteredPath[filteredPath.length - 1] || NAV_MENU.Home.id;

        // Track the event with the determined page name
        mixpanel.track(`${pageName}.load.completed`);
    };

    const updateMobileWebHeaderTitle = id => {
        let title = camelCase(id);

        switch (id) {
            case 'condition-profile':
                title = 'healthProfile';
                break;
            case 'programs':
                title = 'availablePrograms';
                break;
            case 'reports-and-data':
                title = 'oneAppReportsAndData';
                break;
            case 'user-guides':
                title = 'oneAppUserGuides';
                break;
            case A1C_REPORT:
                title = 'selfReportScreen';
                break;
            case CHOLESTEROL_REPORT:
                title = 'selfReportScreen';
                break;
            case A1C_CHOLESTEROL_COMBINED_REPORT:
                title = 'selfReportScreen';
                break;
            case CARDIOMETABOLIC_LAB_KIT_FORM:
                title = 'cardiometabolicLabKitForm';
                break;
            case 'cardio-health-insights-pillar':
            case 'cardio-landing-page':
                title = 'healthInsights';
                break;
            case 'care-guide-activation':
                title = 'careGuideActivation';
                break;
        }

        return title;
    };

    const isMentalHealthHeaderPath = useCallback(() => {
        return (
            pathname?.includes(MENTAL_HEALTH_PREFIX) &&
            !pathname?.includes(
                NONSTANDARD_MENTAL_HEALTH_ROUTES.Interstitial
            ) &&
            !pathname?.includes(NONSTANDARD_MENTAL_HEALTH_ROUTES.Onboarding) &&
            !pathname?.includes(
                NONSTANDARD_MENTAL_HEALTH_ROUTES.Reassessment
            ) &&
            !pathname?.includes(NONSTANDARD_MENTAL_HEALTH_ROUTES.Login) &&
            !pathname?.includes(MESSAGE_CENTER_PATH)
        );
    }, [pathname]);

    const isMentalHealthSidebarPath = useCallback(() => {
        return (
            isMentalHealthHeaderPath() &&
            !locationWithHash?.includes(
                NONSTANDARD_MENTAL_HEALTH_ROUTES.WaitingRoomHash
            )
        );
    }, [isMentalHealthHeaderPath, locationWithHash]);

    const isMentalHealthNonSidebarPath = useCallback(() => {
        return (
            pathname?.includes(MENTAL_HEALTH_PREFIX) &&
            !isMentalHealthSidebarPath()
        );
    }, [isMentalHealthSidebarPath, pathname]);

    /* eslint-disable camelcase */
    const screenHeadersObject = {
        ProgramsScreen: t(`sidebar.navigation.mentalHealthSleepProgram`),
        add_entry: t(`sidebar.navigation.mentalHealthSleepAddEntry`),
        completedSleepEntry: t(`sidebar.navigation.mentalHealthSleepAddEntry`),
        create_schedule: t(
            `sidebar.navigation.mentalHealthSleepCreateSchedule`
        ),
        weekly_checkin: t(`sidebar.navigation.mentalHealthSleepWeeklyCheckin`),
        edit_schedule: t(`sidebar.navigation.mentalHealthSleepEditSchedule`),
        week_entries: t(`sidebar.navigation.mentalHealthSleepWeekEntries`),
    };
    /* eslint-enable camelcase */

    const screenSpecificHeaderTitle =
        CommonUtils.getHeaderTitleForMatchingScreen(hash, screenHeadersObject);

    const enableTitleAndHamburger = useCallback(() => {
        const mhPath3 = isMentalHealthHeaderPath() ? pathNameWTOSlash[3] : null;
        const id =
            mhPath3 ||
            pathNameWTOSlash[2] ||
            pathNameWTOSlash[1] ||
            'condition-management';

        if (
            SELF_REPORT_SCREEN.includes(id) &&
            setShowHamburger &&
            setHeaderTitle &&
            setShowLeftIcon
        ) {
            setShowHamburger(false);
            setShowLeftIcon(true);

            if (id === CARDIOMETABOLIC_LAB_KIT_FORM) {
                setHeaderTitle(
                    t(`sidebar.navigation.cardiometabolicLabKitForm`)
                );
            } else {
                setHeaderTitle(t(`sidebar.navigation.selfReportScreen`));
            }
        } else if (setShowHamburger && setHeaderTitle) {
            setShowHamburger(true);
            setShowLeftIcon(false);
            setHeaderTitle(
                t(`sidebar.navigation.${updateMobileWebHeaderTitle(id)}`)
            );

            if (
                id === 'home' &&
                pathNameWTOSlash[1] === 'condition-management'
            ) {
                setHeaderTitle(t(`sidebar.title.conditionManagement`));
            } else if (screenSpecificHeaderTitle) {
                return setHeaderTitle(screenSpecificHeaderTitle);
            } else if (isMentalHealthHeaderPath()) {
                setHeaderTitle(
                    t(
                        `sidebar.navigation.mentalHealth${startCase(
                            id
                        )?.replaceAll(' ', '')}`
                    )
                );
            } else {
                setHeaderTitle(
                    t(`sidebar.navigation.${updateMobileWebHeaderTitle(id)}`)
                );
            }
        }
    }, [
        t,
        pathNameWTOSlash,
        isMentalHealthHeaderPath,
        screenSpecificHeaderTitle,
    ]);

    const enableBackArrowWithTitle = useCallback(
        relativePath => {
            if (setShowHamburger && setHeaderTitle && setShowLeftIcon) {
                setShowHamburger(false);
                setShowLeftIcon(true);

                if (relativePath === 'download-app') {
                    setHeaderTitle(t(`oneapp.downloadApp`));
                } else if (relativePath === 'all-tasks') {
                    setHeaderTitle(t(`oneapp.allTasks`));
                } else if (relativePath === 'task-detail') {
                    // don't set header title for task-detail.
                } else if (relativePath === 'start-glp1-journey') {
                    setHeaderTitle(t(`oneapp.startGlp1Journey`));
                } else if (relativePath === 'care-guide-activation') {
                    setHeaderTitle(t(`oneapp.careGuideActivation`));
                } else {
                    setHeaderTitle(
                        t(`sidebar.navigation.healthInsightsReports`)
                    );
                }
            }
        },
        [t]
    );

    const isValidSidebarPath = useCallback(() => {
        const documentLocation = `${document.location.pathname}${document.location.hash}`;

        return (
            documentLocation?.includes(CONDITION_MANAGEMENT_PREFIX) ||
            isMentalHealthSidebarPath(documentLocation)
        );
    }, [isMentalHealthSidebarPath]);

    const updateSidebarState = useCallback(() => {
        if (breakpoint !== BREAKPOINT_SIZE.Small && isValidSidebarPath()) {
            CommonUtils.updateSidebar(true, dispatch);
        } else {
            CommonUtils.updateSidebar(false, dispatch);
        }
    }, [breakpoint, isValidSidebarPath, dispatch]);

    const resetSideNavBar = useCallback(
        evt => {
            if (!evt.executed) {
                updateSidebarState();
                evt.executed = true;
            }
        },
        [updateSidebarState]
    );

    const updateUnreadCounter = response => {
        if (response?.unread_messages_count) {
            const {telemed, mental_health: myStrength} =
                response.unread_messages_count;

            const channelMsgInfo = channels[0];
            const foodLoggerInfo = channels[1];

            dispatch(
                chatUpdateUnreadCount({
                    hasUnread: {
                        telemed: telemed > 0,
                        myStrength: myStrength > 0,
                        chronicCare: channelMsgInfo?.unreadMessageCount > 0,
                        foodLogger: foodLoggerInfo?.unreadMessageCount > 0,
                    },
                })
            );

            const setCounterExpirationTime = addMinutes(
                new Date(),
                MESSAGE_COUNTER_MINUTES_TTL
            );

            CommonUtils.setLocalStorage({
                key: MESSAGE_COUNTER_EXPIRATION,
                value: setCounterExpirationTime,
            });
        }
    };

    const handleMemberNavigationResponse = response => {
        updateUnreadCounter(response);

        if (isNil(response?.navigation)) {
            return;
        }

        dispatch(
            appUpdate({
                hasConditionManagement: Boolean(
                    response?.navigation[OFFERED_PROGRAMS.ConditionManagement]
                ),
            })
        );

        StorageUtils.remove({
            key: IS_TDOC_TOKEN_INVALID,
        });
    };

    useEffect(() => {
        AmplitudeClient.identify(OneAppUserAPI.getMemberId(), {
            impersonation: ImpersonationUtils.isImpersonating(),
        });
    }, [impersonationInfo]);

    // using useMemo to Memoize the impersonation route handler (no side effects)
    const memoizedImpersonationLogic = useMemo(() => {
        if (ImpersonationUtils.isImpersonating()) {
            const impersonatorType = impersonationInfo?.impersonatorType;
            const extendedRoutes = [
                ...relativeRoutes.current,
                ...messageCenterRoutes,
            ];

            return () =>
                ImpersonationUtils.impersonationRouteLogic(
                    impersonatorType,
                    extendedRoutes
                );
        }

        return null;
    }, [impersonationInfo, relativeRoutes]);

    // Trigger the memoized function directly during render if it exists
    if (memoizedImpersonationLogic) {
        memoizedImpersonationLogic();
    }

    /* start: events triggered on Initial App Load */
    const triggerMessageEndpoint =
        CommonUtils.validateRedirectURL(locationWithHash) ||
        !isBefore(
            new Date(),
            new Date(CommonUtils.getLocalStorage(MESSAGE_COUNTER_EXPIRATION))
        ) ||
        false;

    useEffect(() => {
        // Store UTM params from URL to cookie for easy inclusion and use by API requests
        persistUtmCookie();
    }, []);

    useEffect(() => {
        if (OneAppUserAPI.getTdocAuthToken()) {
            OneAppUserAPI.getMemberNavigation()
                .then(response => {
                    updateUnreadCounter(response);

                    if (isNil(response?.navigation)) {
                        return;
                    }
                    // sample JSON response for member_navigation API
                    // member_navigation : {"navigation":{"Condition Management": ["DIABETES", "HYPERTENSION"],"Mental Health Support": ["DIGITAL","COMPLETE"],
                    //   "Telemed": ["Primary Care team","Primary Care plan","Expert Medical Opinion cases", "Primary Care vitals"]},
                    //   "branding":{"brand_code": "teladoc", "brand_name": "Teladoc", "bundle_identifier": "com.teladoc",
                    //   "consent_agreement_url": {"en-us": "https://goto.integration.livongo.com/web-privacy-policy?locale=en-US",
                    //   "es-us": "https://goto.integration.livongo.com/web-privacy-policy?locale=es-US"}, "logo_path": "registration/common/Teladoc_Logo_ES.svg",
                    //   "favicon_url": "https://www.teladoc.com/wp-content/themes/teladoc-members/media/images/icon.gif", "non_discrimination_url": {
                    //   "en-us": "https://goto.integration.livongo.com/notice-of-non-discrimination?locale=en-US",
                    //   "es-us": "https://goto.integration.livongo.com/notice-of-non-discrimination?locale=es-US"}, "organization": "Teladoc Health, Inc.",
                    //   "terms_of_service_url": {"en-us": "https://goto.integration.livongo.com/terms-of-service?locale=en-US",
                    //   "es-us": "https://goto.integration.livongo.com/terms-of-service?locale=es-US"}, "video_url": "https://member.teladoc.com/test_video"}}
                    dispatch(
                        appUpdate({
                            isFepCcmMember: response?.is_fep_ccm_member,
                            isFepEnabled: response?.is_fep_enabled,
                            isMinorDependent: response?.is_minor_dependent,
                            hasCareTeam: response?.telemed_inbox,
                            branding: response?.branding,
                            hasHABot: response?.health_assistant,
                            healthAssistantData:
                                response?.health_assistant_data,
                            hasTelemed: {
                                primaryCarePlan:
                                    response?.navigation?.Telemed?.includes(
                                        OFFERED_PROGRAMS.PrimaryCarePlan
                                    ) || false,
                                primaryCareTeam:
                                    response?.navigation?.Telemed?.includes(
                                        OFFERED_PROGRAMS.PrimaryCareTeam
                                    ) || false,
                                expertOpinionCases:
                                    response?.navigation?.Telemed?.includes(
                                        OFFERED_PROGRAMS.ExpertMedicalOpinion
                                    ) || false,
                                primaryCareVitals:
                                    response?.navigation?.Telemed?.includes(
                                        OFFERED_PROGRAMS.PrimaryCareVitals
                                    ) || false,
                                myMentalHealth:
                                    response?.navigation?.Telemed?.includes(
                                        OFFERED_PROGRAMS.MyMentalHealth
                                    ) || false,
                            },
                            hasConditionManagement: Boolean(
                                response?.navigation[
                                    OFFERED_PROGRAMS.ConditionManagement
                                ]
                            ),
                            hasMentalHealth: Boolean(
                                response?.navigation[
                                    OFFERED_PROGRAMS.MentalHealth
                                ]
                            ),
                            hasPartnerPrograms: Boolean(
                                response?.navigation[
                                    OFFERED_PROGRAMS.PartnerPrograms
                                ]
                            ),
                            hasNewCCMNavigation:
                                response?.navigation.new_ccm_navigation_menu,
                            hasGetCareNavigation:
                                response?.navigation.top_nav_get_care,
                            isGetCareButtonPrimary:
                                response?.navigation.top_nav_get_care_primary,
                            isActivityTrackerEnabled: Boolean(
                                false // response?.is_health_data_enabled [SHINE-1275] Suppressing Activity Tracker route as per the latest requirement we only left access via link
                            ),
                            hasCCMHealthInsights:
                                response?.navigation.ccm_health_insights,
                            hasCommunicationPreferencesPulse:
                                response?.navigation
                                    .communication_preferences_pulse,
                        })
                    );

                    StorageUtils.remove({
                        key: IS_TDOC_TOKEN_INVALID,
                    });
                })
                .catch(error => {
                    const errorCode =
                        error?.response?.status ||
                        error?.response?.data?.error ||
                        'unknown_error';

                    const errorMessage =
                        error?.response?.data?.error_description ||
                        error?.response?.statusText ||
                        'Failed to load member navigation';
                    // Track error in Amplitude
                    /* eslint-disable camelcase */

                    AmplitudeClient.track(TRACK_EVENTS_MAP.errorView, {
                        error_code: errorCode,
                        error_message: errorMessage,
                        category: CCM_EVENT_PROPS.category,
                        product_line: CCM_EVENT_PROPS.productLine,
                        error_type: ERROR_TRACKING_COMPONENTS.OneappRoot,
                    });
                    /* eslint-enable camelcase */

                    if (
                        errorMessage?.toLowerCase()?.includes(AUTHENTICATION) ||
                        error
                            ?.toString()
                            ?.toLowerCase()
                            ?.includes(AUTHENTICATION)
                    ) {
                        StorageUtils.set({
                            key: IS_TDOC_TOKEN_INVALID,
                            value: true,
                        });
                        setShowInvalidSessionModal(true);
                    }
                });
            dispatch(
                userUpdate({userLocale: CommonUtils.getOneAppLocaleCookie()})
            );
            setProtoHeaders({
                'Accept-Language': CommonUtils.getOneAppLocaleCookie(),
            });
            changeLanguage(CommonUtils.getOneAppLocaleCookie());
            ApptentiveUtils.event('oneapp.loaded');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isInitialUserLocaleRef.current) {
            isInitialUserLocaleRef.current = false;

            return;
        }

        if (userLocale) {
            OneAppUserAPI.getMemberNavigation()
                .then(response => {
                    dispatch(
                        appUpdate({
                            branding: response?.branding,
                        })
                    );
                })
                .catch(error => {
                    const errorMessage =
                        error?.response?.data?.error_description ||
                        error?.response?.statusText ||
                        'Failed to load member navigation';

                    if (
                        errorMessage?.toLowerCase()?.includes(AUTHENTICATION) ||
                        error
                            ?.toString()
                            ?.toLowerCase()
                            ?.includes(AUTHENTICATION)
                    ) {
                        StorageUtils.set({
                            key: IS_TDOC_TOKEN_INVALID,
                            value: true,
                        });
                        setShowInvalidSessionModal(true);
                    }
                });
        }
    }, [dispatch, userLocale]);

    useEffect(() => {
        window.engagement?.updateLanguage?.(selectedLang);
    }, [selectedLang]);

    useEffect(() => {
        if (
            (triggerMessageEndpoint || triggerUnreadCounter) &&
            OneAppUserAPI.getTdocAuthToken() &&
            CommonUtils.getLocalStorage(MESSAGE_COUNTER_EXPIRATION)
        ) {
            setTriggerUnreadCounter(false);
            OneAppUserAPI.getMemberNavigation()
                .then(response => {
                    updateUnreadCounter(response);
                    handleMemberNavigationResponse(response);

                    StorageUtils.remove({
                        key: IS_TDOC_TOKEN_INVALID,
                    });
                })
                .catch(exception => {
                    if (
                        exception
                            ?.toString()
                            ?.toLowerCase()
                            ?.includes(AUTHENTICATION)
                    ) {
                        StorageUtils.set({
                            key: IS_TDOC_TOKEN_INVALID,
                            value: true,
                        });
                        setShowInvalidSessionModal(true);
                    }
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [triggerMessageEndpoint, triggerUnreadCounter]);
    /* end: events triggered on Initial App Load */

    // event to Handle Browser Back & Forward button click
    useEffect(() => {
        window.addEventListener('popstate', resetSideNavBar);

        return () => {
            window.removeEventListener('popstate', resetSideNavBar);
        };
    }, [resetSideNavBar]);

    // DTP authenticated visits use UCS which expect the full screen so hiding the header and footer
    useEffect(() => {
        const handleHashChange = () => {
            if (
                hash?.includes('WaitingPage') ||
                hash?.includes('CommSessionPage')
            ) {
                setHideHeaderFooter(true);
            } else if (
                hash?.includes('ThankYou') ||
                hash?.includes('DtpVisitCancelled') ||
                hash?.includes('WaitingRoom')
            ) {
                setHideHeaderFooter(false);
            }
        };

        handleHashChange();

        window.addEventListener('hashchange', handleHashChange);

        return () => {
            window.removeEventListener('hashchange', handleHashChange);
        };
    }, [hash]);

    // To Handle The Resizing event of browser
    useEffect(() => {
        updateSidebarState();
    }, [breakpoint, dispatch, pathname, updateSidebarState]);

    // TODO: Not sure if this can be combined with the above. Needs more analysis
    useEffect(() => {
        if (isValidSidebarPath()) {
            if (breakpoint === BREAKPOINT_SIZE.Small) {
                enableTitleAndHamburger();
            } else if (!isOneAppSidebar) {
                CommonUtils.updateSidebar(true, dispatch);
            }
        }

        const backArrowPath = BACKARROW_PATHS.find(path =>
            pathNameWTOSlash.includes(path)
        );

        if (backArrowPath && breakpoint === BREAKPOINT_SIZE.Small) {
            enableBackArrowWithTitle(backArrowPath);
        } else {
            setShowLeftIcon(false);
        }
    }, [
        dispatch,
        breakpoint,
        isOneAppSidebar,
        pathNameWTOSlash,
        isValidSidebarPath,
        enableTitleAndHamburger,
        weightScaleConfirmation,
        enableBackArrowWithTitle,
    ]);

    useEffect(() => {
        const handleResize = debounce(
            () => CommonUtils.setBreakpoint(breakpoint, dispatch),
            500
        );

        window.addEventListener('resize', handleResize);

        return () => {
            handleResize.cancel();
            window.removeEventListener('resize', handleResize);
        };
    }, [breakpoint, dispatch]);

    const onStayClick = () => {
        setShowIdleModal(false);
    };

    // This is used to refresh the user's education session when it times out due to inactivity
    const refreshEducationSession = useCallback(() => {
        ChronicCareCommonUtils.openEducationPortal({
            activePrograms,
            isOneApp: true,
        });
    }, [activePrograms]);

    useEffect(() => {
        if (redirectToEducation) {
            refreshEducationSession();
        }
    }, [redirectToEducation, refreshEducationSession]);

    if (
        !OneAppUserAPI.getTdocAuthToken() ||
        !OneAppUserAPI.getBundleIdentifier()
    ) {
        redirectToLoginPage(true, branding?.member_host);
    }

    // TODO: Temporary fix for 1/1
    if (pathname.includes('help/faq')) {
        window.open(FAQ_URL, '_blank');

        window.history.pushState({}, '', HOME_PATH);
    }

    if (pathname.includes('terms/cr_agreement')) {
        window.open(CARE_RECIPIENT_AGREEMENT_URL, '_blank');
    }

    // TODO: Refactor this logic as part of TIME-790
    if (pathname?.includes('communication-preferences') && showHamburger) {
        setShowHamburger(false);
    }

    useG11nBidirectionalStateManager(
        userLocale,
        useCallback(
            code => {
                dispatch(userUpdate({userLocale: code}));
            },
            [dispatch]
        )
    );

    const redirectToMedoptSignup = () => {
        document.writeln(t(`redirecting.goTo`));

        const oneAppParams = CommonUtils.generateOneAppParams(ONEAPP_HOME_PATH);

        window.location.href = `${UserAPI.getSignupUrl({
            signupUrl:
                window?.process?.env?.SIGNUP_URL ?? process.env.SIGNUP_URL,
            locale: userLocale,
            medOptFlavor: pathname.includes(MEDOPT_WM_DPP_SIGNUP),
        })}${oneAppParams}`;
    };

    const waitThenRedirect = ({timeoutInMillis, numWaits}) => {
        let timesWaited = 0;

        const intervalId = setInterval(() => {
            if (timesWaited >= numWaits) {
                clearInterval(intervalId);

                window.location.reload();
            }

            if (!isNil(OneAppUserAPI.getCurrentCcmAccessToken())) {
                clearInterval(intervalId);

                redirectToMedoptSignup();
            } else {
                timesWaited++;
            }
        }, timeoutInMillis);
    };

    // Redirect members to the medopt-signup page
    useEffect(() => {
        if (isMedOptSignupPath) {
            waitThenRedirect({timeoutInMillis: 500, numWaits: 60});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMedOptSignupPath]);

    useEffect(() => {
        if (isMentalHealthNonSidebarPath(pathname)) {
            setHideMobileTopHeader(true);
            setShowHamburger(false);
            setHeaderTitle('');
        } else if (
            pathname?.includes(CONDITION_MANAGEMENT_PREFIX) &&
            isEmpty(OneAppUserAPI.getCurrentCcmAccessToken())
        ) {
            const hostname = document.location.hostname;
            const isAllowedHost =
                hostname.endsWith('teladoc.com') ||
                hostname.endsWith('tdoctest.com') ||
                hostname === 'localhost';

            if (isAllowedHost) {
                try {
                    // eslint-disable-next-line node/prefer-global/url
                    const targetUrl = new URL(
                        `/ccm-sso${search || ''}`,
                        window.location.origin
                    );

                    if (
                        targetUrl.protocol === 'http:' ||
                        targetUrl.protocol === 'https:'
                    ) {
                        window.location.href = targetUrl.toString();
                    }
                } catch {
                    // Avoid redirect on invalid URL construction
                }
            }
        } else {
            setHideMobileTopHeader(false);
        }
    }, [pathname, host, search, isMentalHealthNonSidebarPath]);

    useEffect(() => {
        getStore();
        initialLoadEvents();
        dispatch(
            appUpdate({
                isOneApp: true,
                breakpoint:
                    screenWidth <= 767
                        ? BREAKPOINT_SIZE.Small
                        : screenWidth > 767 && screenWidth < 1280
                        ? BREAKPOINT_SIZE.Medium
                        : BREAKPOINT_SIZE.Large,
            })
        );

        dispatch(appUpdate({oneAppActiveNav: NAV_MENU.Home.id}));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    useEffect(() => {
        if (
            hasMentalHealth &&
            (!hasMHGuideAvailable || pathname === MESSAGE_CENTER_PATH)
        ) {
            OneAppUserAPI.hasUserMHGuideAccess().then(hasGuide => {
                dispatch(appUpdate({hasMHGuideAvailable: hasGuide}));
            });
        }
    }, [dispatch, hasMentalHealth, hasMHGuideAvailable, pathname]);

    useEffect(() => {
        // re-dispatch chatConnect to make sure that we're sending along the correct, updated language to Sendbird
        if (hasChatAvailable) {
            dispatch(chatConnect());
        }
    }, [dispatch, hasChatAvailable]);

    useEffect(() => {
        if (isBloodPressureRetakeLink) {
            dispatch(appUpdate({isBloodPressureRetakeFromLink: true}));
        }
    }, [dispatch, isBloodPressureRetakeLink]);

    // The point of this is to change which routes will be available to the app based on a feature flag
    // The condition looks a little messy, but what it does is this:
    // If the route doesn't have any feature flag props, include it in our routes
    // If the route specifically says a feature flag will enable it, keep it if the feature flag is enabled but remove it otherwise
    // If the route specifically says a feature flag will disable it, remove it if the feature flag is enabled but keep it otherwise
    useEffect(() => {
        relativeRoutes.current = [
            ...mainRoutes,
            ...navRoutes,
            ...CCMSubRoute,
        ].filter(
            route =>
                (!route.featureFlagEnable && !route.featureFlagDisable) ||
                route.featureFlagEnable === 'mhWebsdk'
        );
    }, []);

    // Add Apptentive pageload event only to route with Apptentive event.
    useEffect(() => {
        const loadRoute = relativeRoutes?.current?.find(
            route => route.path === pathname
        );

        if (loadRoute?.apptentiveEvent && loadRoute?.id) {
            ApptentiveUtils.event(
                `navigation.page.${loadRoute.id?.toLowerCase()}.loaded`
            );
        }
    }, [pathname]);

    // Handle member navigation refresh triggered by TaskWidget
    // This replaces the need for window.location.href and triggers proper re-initialization
    useEffect(() => {
        if (triggerMemberNavRefresh) {
            // Re-trigger any member-specific initialization logic here
            // This effect will run whenever TaskWidget dispatches member navigation update

            // Example: You can add any member context re-initialization here
            // such as refreshing user data, updating analytics context, etc.

            if (
                (window?.process?.env?.ENVIRONMENT ??
                    process.env.ENVIRONMENT) !== 'production'
            ) {
                // eslint-disable-next-line no-console
                console.log('Root: Member navigation refresh triggered', {
                    timestamp: triggerMemberNavRefresh,
                });
            }
        }
    }, [triggerMemberNavRefresh]);

    // Hide HA Bot based on Pathname for CCM & MH & Messaging screens or if a session is impersonated via agent
    // Show HA Bot if hasHABot flag via member navigation endpoint set to true and above conditions are not satisified.
    useEffect(() => {
        const shouldHideHABot = () => {
            const isPathRestrictedForHABot = [VIDEO_PREFIX].some(prefix =>
                pathname?.includes(prefix)
            );

            const hasImpersonation = Boolean(
                ImpersonationUtils.isImpersonating() || isImpersonation
            );

            const isUCSVideoPage =
                hash?.includes('WaitingPage') ||
                hash?.includes('CommSessionPage');

            return (
                isPathRestrictedForHABot || isUCSVideoPage || hasImpersonation
            );
        };

        const hABotHasConfig = Boolean(
            healthAssistantData?.service_url &&
                healthAssistantData?.developer_name &&
                healthAssistantData?.org_id &&
                healthAssistantData?.bootstrap_src &&
                healthAssistantData?.base_url
        );

        setShowHABot(!shouldHideHABot() && hasHABot && hABotHasConfig);
    }, [hasHABot, pathname, hash, isImpersonation, healthAssistantData]);

    // Intro to Onboarding with Notification Modal
    // Only when we're coming from OneApp Dashboard/GetCare and landing on Condition Management
    useEffect(() => {
        const previouslyFromGetCareDashboard =
            CommonUtils.isOneAppDashboard(previousPath);

        const currentPathCCM = pathname?.includes(CONDITION_MANAGEMENT_PREFIX);

        const onExploreProgram = () => {
            dispatch(
                appUpdate({
                    hasOnboardingPrograms: true,
                })
            );
        };

        // we're coming from OneApp Dashboard/GetCare and landing on Condition Management
        if (previouslyFromGetCareDashboard && currentPathCCM) {
            const isDashboardOfCCM = pathNameWTOSlash?.includes(HOME);

            const hasSeenOnboarding = StorageUtils.get({
                key: ONBOARDING_INTRO_SEEN,
                type: 'session',
            });

            // Only when it's not in Condition Management Dashboard and has not shown Onboarding flow
            // and has not shown the Notification
            if (!isDashboardOfCCM && !hasSeenOnboarding && !onboardingNotify) {
                // Set Notified
                setOnboardingNotify(true);

                OnboardingAPI.display().then(res => {
                    const isOnboardingEnabled = !isOneApp;

                    if (res?.data?.showOneAppCard && isOnboardingEnabled) {
                        notify({
                            type: 'info',
                            autoClose: 10000, // auto close in 10 second
                            title: t(`onboarding.homeCard.heading`),
                            message: t(`onboarding.homeCard.description`),
                            buttonText: t(`onboarding.homeCard.action`),
                            onClick: () => {
                                onExploreProgram();
                            },
                        });
                    }
                });
            }
        }
    }, [
        t,
        dispatch,
        pathname,
        isOneApp,
        previousPath,
        pathNameWTOSlash,
        onboardingNotify,
    ]);

    useEffect(() => {
        if (isTokenValid) {
            CommonUtils.removeLocalStorage(ACCESS_TOKEN_PREV);
            CommonUtils.removeCookie(ACCESS_TOKEN_COOKIE);
            CommonUtils.removeCookie(REFRESH_TOKEN_COOKIE);
            OneAppUserAPI.refreshToken();
        }
    }, [isTokenValid]);

    useEffect(() => {
        if (
            isOneApp &&
            !pathname.includes(INBOX_ROUTES.chronicCare) &&
            !isEmpty(CommonUtils.getLocalStorage(SEND_MESSAGE_ONCE))
        ) {
            CommonUtils.removeLocalStorage(SEND_MESSAGE_ONCE);
        }
    }, [pathname, isOneApp]);

    // Listens to Apptentive "Launch" event then handle focus
    useEffect(() => {
        const handlers = {};

        APPTENTIVE_EVENTS.forEach(eventType => {
            const handler = e =>
                ApptentiveUtils.handleFocus(
                    ApptentiveUtils.findElementFromEventType(eventType)
                );

            handlers[eventType] = handler;
            document.addEventListener(eventType, handler);
        });

        // clean up
        return () => {
            APPTENTIVE_EVENTS.forEach(eventType => {
                if (handlers[eventType]) {
                    document.removeEventListener(
                        eventType,
                        handlers[eventType]
                    );
                }
            });
        };
    }, []);

    // Checks DOM for an existing Apptentive modal then handle focus
    useEffect(() => {
        APPTENTIVE_EVENTS.forEach(eventType => {
            const element = ApptentiveUtils.findElementFromEventType(eventType);

            if (element) {
                ApptentiveUtils.handleFocus(element);
            }
        });
    }, []);

    /**
     * Initializing @axe-core/react
     * https://github.com/dequelabs/axe-core-npm/blob/develop/packages/react/README.md
     */
    ReactAxeCore({loadScript: IS_NON_PROD});

    // This window object is used to update UCS MFE via console.
    if (!IS_PROD) {
        window.UCS_CDN_URL = UCS_CDN_LOWER_URL;
        window.UCS_SERVICE_REGISTRY_URL = UCS_SERVICE_REGISTRY_LOWER_URL;
    } else {
        window.UCS_CDN_URL = UCS_CDN_PROD_URL;
        window.UCS_SERVICE_REGISTRY_URL = UCS_SERVICE_REGISTRY_PROD_URL;
    }

    // Monitor hash changes and refresh member navigation when navigating from ChooseCoverage to Dashboard
    const prevHashRef = useRef(hash);

    useEffect(() => {
        if (
            prevHashRef.current === '#ChooseCoverage' &&
            hash === '#Dashboard'
        ) {
            // Trigger member navigation API call to refresh header data
            setTriggerUnreadCounter(true);
        }
        prevHashRef.current = hash;
    }, [hash]);

    useEffect(() => {
        return setupMicroNavigationBridge();
    }, []);

    return (
        <Router>
            {isOneAppUserAuthenticated && (
                <>
                    <RootIdleTimers
                        pathname={pathname}
                        setShowIdleModal={setShowIdleModal}
                        setTriggerUnreadCounter={setTriggerUnreadCounter}
                    />
                    {!hideHeaderFooter && (
                        <Header
                            headerTitle={headerTitle}
                            showLeftIcon={showLeftIcon}
                            showHamburger={showHamburger}
                            setHeaderTitle={setHeaderTitle}
                            onSideNavToggle={onSideNavToggle}
                            setShowHamburger={setShowHamburger}
                            hideMobileTopHeader={hideMobileTopHeader}
                            setIsMobileMenuPage={setIsMobileMenuPage}
                            setHideMobileTopHeader={setHideMobileTopHeader}
                        />
                    )}
                    <main
                        id="mainContent"
                        className={cnb(css.main, {
                            [css.showSidebar]: isOneAppSidebar,
                            [css.mobileNavBGColor]: isMobileMenuPage,
                            [css.mobileMentalHealth]: hideMobileTopHeader,
                            [css.websdkContainer]:
                                oneAppActiveProgram ===
                                FOOTER_CONTENT_STATE.TeleMed.id,
                            [css.impersonationHeight]: isImpersonation,
                            [css.inSession]: hideHeaderFooter,
                        })}
                    >
                        <div id="toast-portal" />
                        {isOneAppSidebar && (
                            <SidebarComponent
                                isOpen={showHamburger}
                                setHeaderTitle={setHeaderTitle}
                                onSideNavToggle={
                                    showHamburger &&
                                    breakpoint === BREAKPOINT_SIZE.Small
                                        ? onSideNavToggle
                                        : null
                                }
                            />
                        )}
                        <div
                            className={cnb(css.content, {
                                [css.hideMobileContent]: isOneAppSidebar,
                            })}
                        >
                            <Suspense fallback={<Loader />}>
                                <Switch>
                                    <Route
                                        exact
                                        path="/condition-management/l/followup"
                                    >
                                        <Redirect to={initialCMRoute} />
                                    </Route>
                                    <Route exact path="/condition-management">
                                        <Redirect to={initialCMRoute} />
                                    </Route>
                                    <Route exact path={`/${MEDOPT_SIGNUP}`}>
                                        <Loader />
                                    </Route>
                                    <Route
                                        exact
                                        path={`/${MEDOPT_WM_DPP_SIGNUP}`}
                                    >
                                        <Loader />
                                    </Route>
                                    {relativeRoutes.current.map(
                                        ({
                                            id,
                                            path,
                                            exact,
                                            programSlugs,
                                            component: Component,
                                            subNavRoutes,
                                            useWebsdkExitPath,
                                            navMenuParentId,
                                            remoteAppName,
                                            remoteWidgetName,
                                        }) => {
                                            const props = {
                                                id,
                                                path,
                                                isOneApp: true,
                                                component: Component,
                                                subNavRoutes,
                                                useWebsdkExitPath,
                                                setHeaderTitle,
                                                setShowHamburger,
                                                setIsMobileMenuPage,
                                                programSlugs,
                                                getOneAppImpersonationUtils:
                                                    ImpersonationUtils,
                                                getOneAppRouteById:
                                                    getPrimaryRouteById,
                                                switchProvider: groupId =>
                                                    OneAppUserAPI.switchProvider(
                                                        groupId
                                                    ),
                                                getNextStepsAPI: NextStepsAPI,
                                                getNextStepsUtils:
                                                    NextStepsUtils,
                                                navMenuParentId,
                                                remoteAppName,
                                                remoteWidgetName,
                                            };

                                            return (
                                                <RoutePrivate
                                                    key={id}
                                                    {...props}
                                                    {...(exact && {exact})}
                                                />
                                            );
                                        }
                                    )}
                                    <Route>
                                        <NoMatch
                                            setShowSidebar={() =>
                                                CommonUtils.updateSidebar(
                                                    false,
                                                    dispatch
                                                )
                                            }
                                            getCareID={getPrimaryRouteById(
                                                NAV_MENU.Home.id
                                            )}
                                        />
                                    </Route>
                                </Switch>
                            </Suspense>
                        </div>
                        {isChatShowing && (
                            <ChatComponent onClose={onChatToggle} />
                        )}
                        {showIdleModal && (
                            <SessionExpiredModalComponent
                                oneAppLogout={OneAppUserAPI.logout}
                                isOpen={showIdleModal}
                                onStay={onStayClick}
                                setShowIdleModal={setShowIdleModal}
                            />
                        )}
                        {showInvalidSessionModal && (
                            <InvalidatedSessionModalComponent
                                oneAppLogout={OneAppUserAPI.logout}
                                isOpen={showInvalidSessionModal}
                            />
                        )}
                    </main>
                    {showHABot && (
                        <HaChatBot
                            userName={healthAssistantData?.member_salutation}
                            serviceUrl={healthAssistantData?.service_url}
                            developerName={healthAssistantData?.developer_name}
                            orgId={healthAssistantData?.org_id}
                            bootstrapSrc={healthAssistantData?.bootstrap_src}
                            baseUrl={healthAssistantData?.base_url}
                        />
                    )}
                    {!hideHeaderFooter && <Footer />}
                    <ScaleConfirmationModal
                        isOpen={weightScaleConfirmation}
                        onRequestClose={onScaleModalToggle}
                    />
                </>
            )}
        </Router>
    );
};

export default Root;
